import { useEffect, useRef, useState } from "react";
import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import AuditLogTextPage from "@/components/auditLog/AuditLogTextPage";
import type { AuditDiffClient } from "@/components/auditLog/auditDiffClient";
import { auditReferenceKey } from "@/components/auditLog/auditReference";
import type {
  AuditComparison,
  AuditField,
  AuditReferenceLabels,
} from "@/components/auditLog/auditLogTypes";
import styles from "@/components/auditLog/auditLog.module.css";

interface Props {
  field: AuditField;
  index: number;
  client: AuditDiffClient;
  references: AuditReferenceLabels;
}

export default function AuditLogFieldComparison({
  field,
  index,
  client,
  references,
}: Props) {
  const { t } = useTranslation();
  const section = useRef<HTMLElement>(null);
  const active = useRef(false);
  const [visible, setVisible] = useState(false);
  const [comparison, setComparison] = useState<AuditComparison | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    if (!section.current) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(section.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let current = true;
    setError(false);
    const load = async () => {
      try {
        const result = await client.request({ type: "select", index });
        if (result.type !== "selected")
          throw new Error("Expected audit comparison");
        if (current) setComparison(result.comparison);
      } catch {
        if (current) setError(true);
      }
    };
    void load();
    return () => {
      current = false;
    };
  }, [client, index, retry, visible]);

  const getPage = async (side: "before" | "after", offset: number) => {
    const result = await client.request({ type: "page", index, side, offset });
    if (result.type !== "page") throw new Error("Expected audit text page");
    if (active.current)
      setComparison((value) => value && { ...value, [side]: result.page });
  };
  const copy = async (side: "before" | "after") => {
    const result = await client.request({ type: "copy", index, side });
    if (result.type !== "copy") throw new Error("Expected audit text");
    if (active.current) await navigator.clipboard.writeText(result.text);
  };

  return (
    <section
      ref={section}
      className={styles.fieldSection}
      aria-label={
        field.name
          ? t(field.name, { defaultValue: field.name })
          : t("audit_diff_original")
      }
    >
      {error ? (
        <Alert
          severity="error"
          action={
            <GenericButton
              size="small"
              variant="outlined"
              onClick={() => setRetry((value) => value + 1)}
            >
              {t("audit_diff_retry")}
            </GenericButton>
          }
        >
          {t("audit_diff_load_failed")}
        </Alert>
      ) : !comparison ? (
        <span role="status">{t("audit_diff_loading")}</span>
      ) : (
        <>
          {comparison.limited && (
            <Alert severity="info">{t("audit_diff_limited")}</Alert>
          )}
          {comparison.operation === "unknown" && (
            <Alert severity="info">{t("audit_diff_unknown")}</Alert>
          )}
          <div
            className={`${styles.comparison} ${
              comparison.operation === "changed" ? styles.changedComparison : ""
            }`}
          >
            {comparison.before && (
              <AuditLogTextPage
                compact
                side="before"
                fieldName={field.name}
                reference={
                  field.reference?.before
                    ? references[
                        auditReferenceKey(
                          field.reference.model,
                          field.reference.before
                        )
                      ]
                    : undefined
                }
                page={comparison.before}
                operation={comparison.operation}
                onPage={(offset) => getPage("before", offset)}
                onCopy={() => copy("before")}
              />
            )}
            {comparison.operation === "changed" && (
              <span className={styles.transitionArrow} aria-hidden="true">
                →
              </span>
            )}
            {comparison.after && (
              <AuditLogTextPage
                compact
                side="after"
                fieldName={field.name}
                reference={
                  field.reference?.after
                    ? references[
                        auditReferenceKey(
                          field.reference.model,
                          field.reference.after
                        )
                      ]
                    : undefined
                }
                page={comparison.after}
                operation={comparison.operation}
                onPage={(offset) => getPage("after", offset)}
                onCopy={() => copy("after")}
                neutral={comparison.operation === "unknown"}
              />
            )}
          </div>
        </>
      )}
    </section>
  );
}
